import { Database, PenLine, X } from "lucide-react";
import React from "react";
import { useFormatting } from "../../hooks/useFormatting";

export type DrillDownType =
  | "cash-position"
  | "runway"
  | "forecast-point"
  | "scenario-min-cash"
  | "scenario-change"
  | "revenue"
  | "expenses"
  | "category-total"
  | "burn-rate"
  | "net-impact"
  | "comparison-cell";

export interface DrillDownData {
  type: DrillDownType;
  title: string;
  mainValue: number;
  date?: string;
  breakdown?: Array<{
    label: string;
    value: number;
    subtext?: string;
    /**
     * Accounting-summary emphasis for the row. Only set by the forecast-point
     * drill-down; other consumers omit it and render with default styling.
     * - `subtotal`: medium weight
     * - `total`: bold (e.g. Closing Balance, Net Change)
     */
    emphasis?: "normal" | "subtotal" | "total";
    /**
     * Accounting-summary top/bottom ruling for the row. Only set by the
     * forecast-point drill-down.
     * - `single`: single top border (above the totals)
     * - `double`: double top+bottom border (around Net Change)
     */
    rule?: "none" | "single" | "double";
  }>;
  calculation?: {
    formula: string;
    steps: Array<{
      label: string;
      value: number | string;
    }>;
  };
  context?: {
    label: string;
    value: string | number;
  }[];
  transactions?: Array<{
    date: string;
    description: string;
    amount: number;
    category: string;
  }>;
  actions?: Array<{
    label: string;
    onClick: () => void;
    /**
     * Visual style for the action button. Defaults to a plain link-style button
     * (existing behaviour). `success` renders a green filled button; `primary`
     * a teal filled button.
     */
    variant?: "link" | "primary" | "success";
  }>;
  // Source provenance — list of contributing data source names for aggregate screens
  sources?: string[];
}

const useDrillDownFormatting = () => {
  const { formatCurrency } = useFormatting();
  return { formatCurrency };
};

interface DrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DrillDownData | null;
  onBack?: () => void;
  canGoBack?: boolean;
}

export const DrillDownModal: React.FC<DrillDownModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { formatCurrency } = useDrillDownFormatting();

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {data.title}
            </h2>
            {data.date && (
              <p className="text-sm text-gray-500 mt-1">{data.date}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Main Value */}
          <div className="mb-6">
            <div className="text-3xl font-bold text-gray-900">
              {formatCurrency(data.mainValue)}
            </div>
          </div>

          {/* Breakdown */}
          {data.breakdown && data.breakdown.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Breakdown
              </h3>
              <div>
                {data.breakdown.map((item, index) => {
                  const hasAccountingStyle =
                    item.emphasis !== undefined || item.rule !== undefined;
                  const isTotal = item.emphasis === "total";
                  const isSubtotal = item.emphasis === "subtotal";
                  const isNegative = item.value < 0;
                  const valueText =
                    hasAccountingStyle && isNegative
                      ? `(${formatCurrency(Math.abs(item.value))})`
                      : formatCurrency(item.value);
                  const rowClass = hasAccountingStyle
                    ? [
                        "flex items-center justify-between py-2",
                        item.rule === "single"
                          ? "border-t border-gray-300 mt-1"
                          : "",
                        item.rule === "double"
                          ? "border-y-[3px] border-double border-gray-400 mt-1"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")
                    : "flex items-center justify-between py-2 border-b border-gray-100";
                  return (
                    <div key={index} className={rowClass}>
                      <div>
                        <div
                          className={`text-sm text-gray-900 ${
                            isTotal
                              ? "font-bold"
                              : isSubtotal
                                ? "font-medium"
                                : ""
                          }`}
                        >
                          {item.label}
                        </div>
                        {item.subtext && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.subtext}
                          </div>
                        )}
                      </div>
                      <div
                        className={`text-sm text-right tabular-nums text-gray-900 ${
                          isTotal ? "font-bold" : "font-semibold"
                        } ${hasAccountingStyle && isNegative ? "text-red-600" : ""}`}
                      >
                        {valueText}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calculation */}
          {data.calculation && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Calculation
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-3 font-mono">
                  {data.calculation.formula}
                </div>
                <div className="space-y-2">
                  {data.calculation.steps.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{step.label}:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof step.value === "number"
                          ? formatCurrency(step.value)
                          : step.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Source Provenance */}
          {data.sources && data.sources.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground mb-3">
                Contributing Data Sources
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.sources.map((source, index) => {
                  const isManual = source.startsWith("Manual Entry");
                  return (
                    <span
                      key={index}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        isManual
                          ? "bg-amber-50 text-amber-700 border border-amber-200"
                          : "bg-blue-50 text-blue-700 border border-blue-200"
                      }`}
                    >
                      {isManual ? (
                        <PenLine className="h-3 w-3" />
                      ) : (
                        <Database className="h-3 w-3" />
                      )}
                      {source}
                    </span>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                This figure is aggregated across the above source
                {data.sources.length > 1 ? "s" : ""}. Drill into individual
                transactions to verify each record.
              </p>
            </div>
          )}

          {/* Context */}
          {data.context && data.context.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Additional Information
              </h3>
              <div className="space-y-2">
                {data.context.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600">{item.label}:</span>
                    <span className="font-semibold text-gray-900">
                      {typeof item.value === "number"
                        ? formatCurrency(item.value)
                        : item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transactions */}
          {data.transactions && data.transactions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Transactions ({data.transactions.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.transactions.map((txn, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-gray-100"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-900">
                        {txn.description}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {txn.date} • {txn.category}
                      </div>
                    </div>
                    <div
                      className={`text-sm font-semibold ${txn.amount >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      {txn.amount >= 0 ? "+" : ""}
                      {formatCurrency(txn.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          {data.actions &&
            data.actions.map((action, index) => {
              const base =
                "px-4 py-2 text-sm font-medium rounded-lg transition-colors";
              const variantClass =
                action.variant === "success"
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : action.variant === "primary"
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "text-blue-600 hover:text-blue-700 hover:bg-blue-50";
              return (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`${base} ${variantClass}`}
                >
                  {action.label}
                </button>
              );
            })}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
